import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BreadCrumbService } from '../../../shared/services/breadcrumb.service';
import { GridOptions } from 'ag-grid-community';
import {
  AuthService,
  DataService,
  CoreImageViewService,
} from '../../../shared/services';
import * as _ from 'lodash';
import { AssetSummaryComponent } from '../../../shared/components/asset-summary/asset-summary.component';
import {
  formatTriStateBooleanFilterLabel,
  normalizeTriStateBooleanFromField,
} from '../../../shared/utils/tri-state-boolean.utils';
import { measureTypeColumn } from '../../../shared/utils/measure-type.utils';

@Component({
  selector: 'app-plans-tasks',
  templateUrl: './plan-tasks.component.html'
})
export class PlanTasksComponent implements OnInit, OnChanges {
  // Modal ref pointer

  gridOptions?: GridOptions;
  // Modal ref pointer
  bsModalRef?: BsModalRef;

  tasksGridAutoGroupColumnDef?: any;

  items: any[] = [];
  assetNames: any = {};
  assetHasImages: any = {};

  isLoading = false;

  @Input() selectedPlan: number = 0;
  /** When true, load tasks from the latest released status (version history). */
  @Input() showReleasedVersionOnly = false;
  isReferenceLoaded = false;
  boolReference?: { value: boolean; name: string }[];
  criticalityReference?: [];
  locationReference?: [];

  constructor(
    private translate: TranslateService,
    public breadCrumbService: BreadCrumbService,
    private data: DataService,
    public auth: AuthService,
    private bsModalService: BsModalService,
    private coreImageView: CoreImageViewService
  ) {}

  async ngOnInit(): Promise<void> {
    this.breadCrumbService.set(
      [
        {
          name: this.translate.instant('navigations.breadcrumbs.modules'),
          link: '/modules'
        },
        {
          name: this.translate.instant('navigations.breadcrumbs.plans'),
          link: '/routing/plans'
        }
      ],
      2
    );

    this.gridOptions = {
      getRowId: (row: any) => row.data.routing_plan_task_id,
      suppressRowClickSelection: true,
      groupSelectsChildren: true,
      rowSelection: 'multiple',
      rowHeight: 30,
      headerHeight: 30,
      animateRows: true,
      floatingFiltersHeight: 30,
      enableCellTextSelection: false,
      suppressContextMenu: true,
      groupDefaultExpanded: -1,
      defaultColDef: {
        filter: true,
        sortable: true,
        resizable: true,
        floatingFilter: true
      },
      columnDefs: [
        {
          headerName: this.translate.instant('headers.routing.plan.plan'),
          field: 'plan_name',
          rowGroup: true,
          filter: 'agTextColumnFilter',
          hide: true
        },
        {
          headerName: this.translate.instant('headers.location.location'),
          field: 'location_name',
          rowGroup: true,
          filter: 'agTextColumnFilter',
          hide: true
        },
        {
          headerName: this.translate.instant('headers.asset.asset'),
          field: 'asset_id',
          rowGroup: true,
          filter: 'agTextColumnFilter',
          hide: true,
          filterValueGetter: (params: any): string => {
            return params.data?.asset_name;
          }
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.component.component'
          ),
          field: 'component',
          rowGroup: true,
          filter: 'agTextColumnFilter',
          hide: true
        },
        {
          headerName: this.translate.instant(
            'headers.fastpm.pm_model.description'
          ),
          field: 'description',
          filter: 'agTextColumnFilter'
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.frequency.frequency'
          ),
          field: 'frequency'
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.labor.labor'
          ),
          field: 'trade'
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.activity.activity'
          ),
          field: 'activity'
        },
        {
          headerName: this.translate.instant(
            'headers.fastpm.pm_model.duration'
          ),
          field: 'duration'
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.state.state'
          ),
          field: 'state'
        },
        {
          headerName: this.translate.instant(
            'headers.fastpm.pm_model.criteria'
          ),
          field: 'criteria'
        },
        { ...measureTypeColumn(this.translate) },
        {
          headerName: this.translate.instant('headers.fastpm.pm_model.keyword'),
          field: 'keyword'
        },
        {
          headerName: this.translate.instant('headers.fastpm.pm_model.proc'),
          field: 'procedure'
        },
        {
          headerName: this.translate.instant('headers.fastpm.pm_model.comment'),
          field: 'comment'
        },
        {
          headerName: this.translate.instant(
            'headers.fastpm.pm_model.justification'
          ),
          field: 'justification'
        },
        {
          headerName: this.translate.instant('headers.routing.task.id'),
          field: 'id',
          hide: true
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.state.id'
          ),
          field: 'stateId',
          hide: true
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.frequency.id'
          ),
          field: 'frequencyId',
          hide: true
        },
        {
          headerName: this.translate.instant(
            'headers.tasks_parameters.labor.id'
          ),
          field: 'tradeId',
          hide: true
        }
      ]
    };
    this.tasksGridAutoGroupColumnDef = {
      headerName: this.translate.instant('headers.fastpm.pm_model.group'),
      field: 'component',
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
      cellRendererParams: {
        innerRenderer: (params: any): any => {
          if (params.node?.field !== 'asset_id') {
            return params.value;
          }

          if (!this.assetNames?.[params.value]) {
            return params.value;
          }

          const wrapper = document.createElement('span');
          const aTag = document.createElement('a');
          aTag.href = 'javascript:void(0)';
          aTag.onclick = () => {
            this.showSummaryModal(params.value);
          };
          aTag.textContent = this.assetNames[params.value];
          wrapper.appendChild(aTag);
          this.coreImageView.appendEyeIcon(
            wrapper,
            !!this.assetHasImages?.[params.value],
            'asset',
            params.value,
            this.assetNames[params.value]
          );
          return wrapper;
        }
      }
    };
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.selectedPlan > 0) {
      await this.loadItems();
      return;
    }
    this.items = [];
  }

  async loadItems(): Promise<void> {
    this.isLoading = true;
    this.items = await this.data
      .post('api/plans/tasks', {
        plan_id: this.selectedPlan,
        released_version: this.showReleasedVersionOnly
      })
      .toPromise();
    this.setGroupDisplayData(this.items);
    this.isLoading = false;
  }

  private setGroupDisplayData(items: any[]): void {
    this.assetNames = {};
    this.assetHasImages = {};
    items.forEach((item: any) => {
      this.assetNames[item.asset_id] = item.asset_name;
      this.assetHasImages[item.asset_id] = !!item.asset_has_image;
    });
  }

  async showSummaryModal(assetId: number): Promise<void> {
    await this.loadReferences();

    this.bsModalRef = this.bsModalService.show(AssetSummaryComponent, {
      initialState: {
        assetId
      },
      class: 'modal-lg'
    });
  }

  async loadReferences(): Promise<void> {
    // Bool
    this.boolReference = [
      { value: true, name: this.translate.instant('navigations.yes') },
      { value: false, name: this.translate.instant('navigations.no') }
    ];

    const orderByName = { orderBy: { name: 'asc' } };
    const orderByThreshold = { orderBy: { threshold: 'asc' } };
    // Criticality
    this.criticalityReference = await this.data
      .by('api/config/criticalities', {
        criteria: {
          account: [this.auth.getAccountId()]
        },
        ...orderByThreshold
      })
      .toPromise();

    // Location
    this.locationReference = await this.data
      .by('api/map/locations', {
        criteria: {
          site: [this.auth.getSiteId()]
        },
        ...orderByName
      })
      .toPromise();

    // Mark ref as loaded (prevent reload)
    this.isReferenceLoaded = true;
  }
}
